import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Sparkles, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api, formatApiError } from "@/lib/api";
import { CATEGORIES, ROLES } from "@/lib/constants";

export function AiDraftDialog({ open, onClose, initial, onDraft }) {
  const [notes, setNotes] = useState("");
  const [category, setCategory] = useState("");
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setCategory(initial.category || "");
    setRoles(initial.applicable_roles || []);
  }, [open, initial.category, initial.applicable_roles]);

  const generate = async () => {
    if (notes.trim().length < 10) return toast.error("Add a few sentences of notes first");
    if (!category || !roles.length) return toast.error("Choose a category and at least one role");
    setLoading(true);
    try {
      const { data } = await api.post("/ai/sop-draft", { notes, category, applicable_roles: roles });
      onDraft({ ...data, category, applicable_roles: roles });
      setNotes("");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !loading && !o && onClose()}>
      <DialogContent className="max-w-xl" data-testid="ai-draft-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display"><Sparkles className="h-4 w-4 text-lasso-deep" /> Draft an SOP with AI</DialogTitle>
          <DialogDescription>Paste rough notes. The draft fills the editor for you to review — nothing is published.</DialogDescription>
        </DialogHeader>
        <div className="space-y-5">
          <Textarea rows={8} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="e.g. when a resident wants to add a shed: they need written approval first, max 10x12, must be 5ft from lot line, color matches home, CM inspects after install…" data-testid="ai-draft-notes-input" />
          <div className="grid gap-5 sm:grid-cols-2">
            <div className="space-y-2">
              <Label>Category</Label>
              <Select value={category || undefined} onValueChange={setCategory}>
                <SelectTrigger data-testid="ai-draft-category-select"><SelectValue placeholder="Choose category" /></SelectTrigger>
                <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value} data-testid={`ai-draft-category-${c.value.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{c.value}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Applicable roles</Label>
              {ROLES.map((r) => (
                <label key={r.value} className="flex items-center gap-2 text-sm">
                  <Checkbox checked={roles.includes(r.value)} onCheckedChange={() => setRoles(roles.includes(r.value) ? roles.filter((x) => x !== r.value) : [...roles, r.value])} data-testid={`ai-draft-role-${r.value}`} /> {r.label}
                </label>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <button onClick={onClose} disabled={loading} className="rounded-full border border-lasso-line px-5 py-2.5 text-sm font-semibold" data-testid="ai-draft-cancel">Cancel</button>
          <button onClick={generate} disabled={loading} className="inline-flex items-center gap-2 rounded-full bg-lasso-ink px-5 py-2.5 text-sm font-semibold text-white hover:bg-lasso-navy disabled:opacity-60" data-testid="ai-draft-generate">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} {loading ? "Drafting…" : "Generate draft"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function AiImproveDialog({ open, onClose, form, onAccept }) {
  const [suggestion, setSuggestion] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    setSuggestion(null);
    setError(null);
    api.post("/ai/sop-improve", { title: form.title, category: form.category, summary: form.summary, purpose: form.purpose, steps: form.steps, applicable_roles: form.applicable_roles })
      .then((r) => setSuggestion(r.data))
      .catch((e) => setError(formatApiError(e)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[88vh] max-w-2xl overflow-y-auto" data-testid="ai-improve-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display"><Sparkles className="h-4 w-4 text-lasso-deep" /> Suggested improvement</DialogTitle>
          <DialogDescription>Review the AI's rewrite. Accepting replaces the editor content — you still need to save.</DialogDescription>
        </DialogHeader>
        {!suggestion && !error && <div className="flex items-center gap-2 py-10 text-sm text-muted-foreground" data-testid="ai-improve-loading"><Loader2 className="h-4 w-4 animate-spin" /> Rewriting for clarity…</div>}
        {error && <p className="rounded-lg bg-rose-50 p-4 text-sm text-rose-800" data-testid="ai-improve-error">{error}</p>}
        {suggestion && (
          <div className="space-y-4 text-sm" data-testid="ai-improve-suggestion">
            <p className="font-display text-lg font-bold">{suggestion.title}</p>
            <p className="text-zinc-600">{suggestion.summary}</p>
            <p className="rounded-xl bg-lasso-tint p-3 text-lasso-navy"><strong>Purpose:</strong> {suggestion.purpose}</p>
            <ol className="space-y-3">
              {suggestion.steps.map((s, i) => (
                <li key={i} className="rounded-xl border border-lasso-line p-3"><p className="font-semibold">{i + 1}. {s.title}</p><p className="mt-1 text-zinc-600">{s.detail}</p></li>
              ))}
            </ol>
            <p className="text-xs text-muted-foreground">Generated by {suggestion.model}</p>
          </div>
        )}
        <DialogFooter>
          <button onClick={onClose} className="rounded-full border border-lasso-line px-5 py-2.5 text-sm font-semibold" data-testid="ai-improve-discard">Discard</button>
          <button disabled={!suggestion} onClick={() => onAccept(suggestion)} className="rounded-full bg-lasso-ink px-5 py-2.5 text-sm font-semibold text-white hover:bg-lasso-navy disabled:opacity-40" data-testid="ai-improve-accept">Accept suggestion</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
